/**
 * API Error Logging Utility
 *
 * Centralized error logging for API routes. Sends errors to:
 * 1. Sentry - Primary alerting and debugging
 * 2. Google Cloud Error Reporting - Redundancy (production only)
 * 3. Console - For Vercel logs
 *
 * Usage:
 * ```typescript
 * import { logApiError } from '@util/logApiError';
 *
 * try {
 *   // API logic
 * } catch (error) {
 *   logApiError(error, req, 'auth_verification');
 *   return res.status(500).json({ error: 'Internal server error' });
 * }
 * ```
 */

import * as Sentry from '@sentry/nextjs';
import type { NextApiRequest } from 'next';
import { serializeError } from 'serialize-error';

/**
 * Generates a random 24-character alphanumeric ID for error tracking.
 * This ID can be shown to users and used to find the error in Sentry/logs.
 */
export const generateApiErrorId = () => {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 24; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Context options for API error logging
 */
export interface ApiErrorContext {
  /** The API action being performed (e.g., 'auth_verification', 'fetch_brands') */
  action?: string;
  /** Additional data to include with the error */
  extra?: Record<string, unknown>;
  /** User ID if known (falls back to cookie-based detection) */
  userId?: string;
}

/**
 * Logs an API error to Sentry, Google Cloud, and console.
 *
 * @param error - The error that occurred
 * @param req - The Next.js API request object (for context extraction)
 * @param actionOrContext - Either a simple action string or full context object
 * @returns The generated error ID for reference
 *
 * @example
 * // Simple usage with just action name
 * logApiError(error, req, 'fetch_user');
 *
 * @example
 * // Full context usage
 * logApiError(error, req, {
 *   action: 'process_payment',
 *   extra: { orderId: '123', amount: 99.99 },
 *   userId: user.id
 * });
 */
export const logApiError = async (
  error: Error | string | unknown,
  req: NextApiRequest,
  actionOrContext?: string | ApiErrorContext
): Promise<string> => {
  const errorId = generateApiErrorId();

  // Normalize context
  const context: ApiErrorContext =
    typeof actionOrContext === 'string'
      ? { action: actionOrContext }
      : actionOrContext || {};

  // Extract request info for context
  const requestContext = {
    path: req.url || 'unknown',
    method: req.method || 'unknown',
    userAgent: req.headers['user-agent'] || 'unknown',
    // Don't log full body as it may contain sensitive data
    hasBody: !!req.body && Object.keys(req.body).length > 0,
  };

  // Try to get user ID from auth cookie if not provided
  const userId = context.userId || extractUserIdFromCookie(req);

  // Always log to console for Vercel logs
  const logPrefix = context.action
    ? `[API:${context.action}]`
    : '[API]';
  console.error(logPrefix, 'Error:', error);
  console.error(logPrefix, 'Request:', requestContext);
  console.error(logPrefix, 'Error ID:', errorId);

  try {
    // Send to Sentry with rich context
    if (error instanceof Error) {
      Sentry.captureException(error, {
        tags: {
          location: 'api',
          action: context.action || 'unknown',
          errorId,
        },
        extra: {
          ...requestContext,
          ...context.extra,
        },
        user: userId ? { id: userId } : undefined,
      });
    } else {
      Sentry.captureMessage(String(error), {
        level: 'error',
        tags: {
          location: 'api',
          action: context.action || 'unknown',
          errorId,
        },
        extra: {
          originalError: error,
          ...requestContext,
          ...context.extra,
        },
        user: userId ? { id: userId } : undefined,
      });
    }

    // Also send to Google Cloud Error Reporting (production only, for redundancy)
    if (process.env.NEXT_PUBLIC_VERCEL_ENV === 'production') {
      await sendToGoogleCloud(error, errorId, req);
    }
  } catch (reportingError) {
    // Don't let error reporting break the API
    console.error('[API] Error while reporting error:', reportingError);
  }

  return errorId;
};

/**
 * Extracts user ID from the auth cookie JWT token.
 * Returns undefined if cookie is missing or invalid.
 */
const extractUserIdFromCookie = (req: NextApiRequest): string | undefined => {
  const token = req.cookies.__session || req.cookies.auth;
  if (!token) return undefined;

  try {
    // JWT tokens have 3 parts separated by dots; payload is the middle part
    const parts = token.split('.');
    if (parts.length !== 3) return undefined;

    const payload = Buffer.from(parts[1], 'base64').toString();
    const parsed = JSON.parse(payload);
    return parsed?.sub || parsed?.user_id || undefined;
  } catch {
    return undefined;
  }
};

/**
 * Sends error to Google Cloud Error Reporting via the /api/error endpoint.
 * This provides redundancy alongside Sentry.
 */
const sendToGoogleCloud = async (
  error: Error | string | unknown,
  errorId: string,
  req: NextApiRequest
): Promise<void> => {
  try {
    // Get the host from the request to construct absolute URL
    const protocol = req.headers['x-forwarded-proto'] || 'https';
    const host = req.headers.host || 'gearfocus.com';
    const errorApiUrl = `${protocol}://${host}/api/error`;

    await fetch(errorApiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...serializeError(error),
        id: errorId,
      }),
    });
  } catch (gcpError) {
    // Log but don't throw - GCP reporting is best-effort
    console.error('[API] Failed to send to Google Cloud:', gcpError);
  }
};
